import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  schema?: any;
}

export const useSEO = ({
  title = 'Atootbandan - Find Your Perfect Life Partner',
  description = 'Discover your soulmate on Atootbandan, India\'s most trusted matrimonial platform. Join millions of verified profiles to find the perfect match.',
  keywords = 'matrimony, marriage, shaadi, bride, groom, wedding, life partner, matchmaking',
  image = '/og-image.jpg',
  url,
  type = 'website',
  author = 'Atootbandan',
  publishedTime,
  modifiedTime,
  schema
}: SEOProps = {}) => {
  const location = useLocation();
  const currentUrl = url || `${window.location.origin}${location.pathname}`;
  const baseUrl = window.location.origin;

  useEffect(() => {
    // Set page title
    document.title = title;

    // Update or create meta tags
    const updateMeta = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Basic meta tags
    updateMeta('description', description);
    updateMeta('keywords', keywords);
    updateMeta('author', author);

    // Safe image URL extraction
    let safeImageUrl = '/og-image.jpg';
    if (typeof image === 'string') {
      safeImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
    } else if (image && typeof image === 'object') {
      const url = (image as any).url || (image as any).path || '/og-image.jpg';
      safeImageUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
    }

    // Open Graph tags
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', currentUrl, true);
    updateMeta('og:type', type, true);
    updateMeta('og:image', safeImageUrl, true);
    updateMeta('og:site_name', 'Atootbandan', true);

    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', safeImageUrl);

    // Article specific tags
    if (publishedTime) {
      updateMeta('article:published_time', publishedTime, true);
    }
    if (modifiedTime) {
      updateMeta('article:modified_time', modifiedTime, true);
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = currentUrl;

    // Structured Data (JSON-LD)
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.type = 'application/ld+json';
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // We don't remove meta tags on unmount to prevent flashing
    };
  }, [title, description, keywords, image, currentUrl, type, author, publishedTime, modifiedTime, schema, baseUrl]);
};

// Helper to generate profile slug from name and id
export const generateProfileSlug = (firstName: string, lastName: string, id: string): string => {
  // Create SEO-friendly slug: name-age-city-profession
  // Example: priya-sharma-28-chennai-software-engineer
  const namePart = `${firstName}-${lastName}`.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  // Add ID at the end for uniqueness
  return `${namePart}-${id}`;
};

// Helper to extract ID from slug
export const extractIdFromSlug = (slug: string): string => {
  // Extract ID from end of slug
  // Example: priya-sharma-28-chennai-software-engineer-675b3456789
  const parts = slug.split('-');
  const lastPart = parts[parts.length - 1];

  // Check if last part is a valid MongoDB ObjectId (24 chars) or numeric ID
  if (lastPart.length === 24 || /^\d+$/.test(lastPart)) {
    return lastPart;
  }

  // Fallback: return the whole slug (backwards compatible)
  return slug;
};

// Generate structured data for profile
export const generateProfileSchema = (profile: any) => {
  const age = calculateAge(profile.dateOfBirth);
  const firstName = profile.firstName || profile.basicInfo?.firstName || profile.userId?.firstName || '';
  const lastName = profile.lastName || profile.basicInfo?.lastName || profile.userId?.lastName || '';

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": `${firstName} ${lastName}`,
    "gender": profile.gender,
    "birthDate": profile.dateOfBirth,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": profile.location?.city || profile.city,
      "addressRegion": profile.location?.state || profile.state,
      "addressCountry": profile.location?.country || profile.country || "India"
    },
    "alumniOf": {
      "@type": "EducationalOrganization",
      "name": profile.education?.college || profile.education?.institution
    },
    "worksFor": {
      "@type": "Organization",
      "name": profile.career?.company || profile.company
    },
    "jobTitle": profile.career?.occupation || profile.occupation,
    "knowsAbout": profile.about || profile.bio,
    "seeks": "Marriage Partner"
  };
};

// Generate structured data for website
export const generateWebsiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Atootbandan",
    "alternateName": "Atootbandan Matrimony",
    "url": window.location.origin,
    "description": "India's most trusted matrimonial platform to find your perfect life partner",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${window.location.origin}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
};

// Generate structured data for organization
export const generateOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Atootbandan",
    "url": window.location.origin,
    "logo": `${window.location.origin}/logo.png`,
    "description": "India's most trusted matrimonial platform",
    "sameAs": [
      "https://www.facebook.com/atootbandan",
      "https://www.twitter.com/atootbandan",
      "https://www.instagram.com/atootbandan"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+91-9999999999",
      "contactType": "Customer Service",
      "areaServed": "IN",
      "availableLanguage": ["English", "Hindi"]
    }
  };
};

// Helper to calculate age
const calculateAge = (dateOfBirth: string) => {
  if (!dateOfBirth) return 25;
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Generate breadcrumb schema
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};